import { useEffect, useSyncExternalStore } from "react";
import { Joyride } from "react-joyride";

const TOUR_OPTIONS = {
  backgroundColor: "var(--surface-secondary)",
  textColor: "var(--text-primary)",
  zIndex: 10000,
  blockTargetInteraction: true,
  buttons: ["close", "primary"],
  overlayClickAction: "close",
};

const TOUR_STYLES = {
  arrow: {
    color: "var(--surface-brand)",
  },
  tooltipTitle: {
    color: "var(--text-primary)",
    padding: "0 10px",
  },
  tooltip: {
    border: "3px solid var(--border-brand)",
    borderRadius: "var(--ds-radius-standard)",
    textAlign: "left",
  },
  tooltipContainer: {
    textAlign: "left",
  },
  tooltipContent: {
    padding: "10px",
  },
  buttonPrimary: {
    background: "var(--surface-brand)",
    borderRadius: "9999px",
    padding: "4px 12px",
    fontSize: "16px",
    fontWeight: 500,
    lineHeight: "24px",
    boxShadow:
      "0 1px 3px 0 rgb(0 0 0 / 0.1), 0 1px 2px -1px rgb(0 0 0 / 0.1)",
  },
};

export const GuidedTour = ({
  steps,
  run,
  stepIndex,
  onEvent,
  skipScroll = true,
  scrollOffset = 20,
  locale = { last: "Finish" },
}) => {
  // Hide tooltip when current step's target is missing from the DOM.
  // Prevents the tooltip from flashing at (0, 0) when a step's anchor
  // element is removed (e.g. during page navigation between tour steps).
  const currentTarget = run ? steps[stepIndex]?.target : null;

  const subscribe = (onStoreChange) => {
    if (!currentTarget) return () => {};

    const observer = new MutationObserver(onStoreChange);
    observer.observe(document.body, {
      childList: true,
      attributes: true,
      subtree: true,
    });
    return () => observer.disconnect();
  };

  const getSnapshot = () => {
    if (!currentTarget) return true;
    return !!document.querySelector(currentTarget);
  };

  const targetExists = useSyncExternalStore(subscribe, getSnapshot);

  // Safety valve: if the target stays missing for 10s while the tour is
  // supposedly running, fire a synthetic close event so useTour's
  // handleCallback calls completeTour() and releases the activeTourName
  // mutex.  Without this, a hidden-but-running tour blocks every other
  // tour for the rest of the session.
  useEffect(() => {
    if (targetExists || !run) return;

    const timer = setTimeout(() => {
      onEvent?.({
        action: "close",
        index: stepIndex,
        status: "running",
        type: "tour:end",
      });
    }, 10_000);

    return () => clearTimeout(timer);
  }, [targetExists, run, onEvent, stepIndex]);

  return (
    <Joyride
      steps={steps}
      continuous={true}
      onEvent={onEvent}
      stepIndex={stepIndex}
      run={run && targetExists}
      locale={locale}
      options={{
        ...TOUR_OPTIONS,
        skipScroll,
        scrollOffset,
      }}
      styles={TOUR_STYLES}
    />
  );
};
